import { type FunctionComponent, type SVGProps, createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";

type SvgIconComponent = FunctionComponent<SVGProps<SVGSVGElement> & { title?: string }>;
type SvgIconSource = SvgIconComponent | string;

type CanvasSvgIconOptions = {
  color: string;
  size: number;
};

const loadedSvgIconCache = new Map<SvgIconSource, Map<string, HTMLImageElement>>();
const loadingSvgIconCache = new Map<SvgIconSource, Map<string, Promise<HTMLImageElement>>>();

function getCacheKey({ color, size }: CanvasSvgIconOptions) {
  return `${size}:${color}`;
}

function getOrCreateCacheEntry<T>(cache: Map<SvgIconSource, Map<string, T>>, icon: SvgIconSource) {
  let entry = cache.get(icon);
  if (!entry) {
    entry = new Map<string, T>();
    cache.set(icon, entry);
  }
  return entry;
}

function buildIconSource(icon: SvgIconSource, { color, size }: CanvasSvgIconOptions) {
  if (typeof icon === "string") {
    return icon;
  }

  const svgMarkup = renderToStaticMarkup(
    createElement(icon, {
      color,
      height: size,
      style: {
        color,
        display: "block",
      },
      width: size,
    }),
  );
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
}

function loadSvgImageAsync(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

export function getCanvasSvgIconImage(icon: SvgIconSource, options: CanvasSvgIconOptions) {
  return getOrCreateCacheEntry(loadedSvgIconCache, icon).get(getCacheKey(options));
}

export function preloadCanvasSvgIconImage(icon: SvgIconSource, options: CanvasSvgIconOptions) {
  const cacheKey = getCacheKey(options);
  const loadedCache = getOrCreateCacheEntry(loadedSvgIconCache, icon);
  const loadedImage = loadedCache.get(cacheKey);
  if (loadedImage) {
    return Promise.resolve(loadedImage);
  }

  const loadingCache = getOrCreateCacheEntry(loadingSvgIconCache, icon);
  const loadingImage = loadingCache.get(cacheKey);
  if (loadingImage) {
    return loadingImage;
  }

  const nextLoad = loadSvgImageAsync(buildIconSource(icon, options)).then((image) => {
    loadedCache.set(cacheKey, image);
    loadingCache.delete(cacheKey);
    return image;
  });

  loadingCache.set(cacheKey, nextLoad);
  return nextLoad;
}
